
import { FileDownloadOutlined, InfoOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardHeader, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { esQuery } from '../../graphql/queries';
import { actionTwoButtonStyle, cardStyle } from '../../theme';


const CampaignCallDetailsDialog = ({ open, data, campaignId, inputStartDate, inputEndDate, segments, campaignName }) => {
	const userContext = useContext(UserContext);
	const client = generateClient();
	const [loading, setLoading] = useState(false);
	const systemAgents = ['0000000', 'System', 'SS Service']

	const [agentRetryDispos, setAgentRetryDispos] = useState([]);
	const [agentFinalDispos, setAgentFinalDispos] = useState([]);
	const [systemRetryDispos, setSystemRetryDispos] = useState([]);
	const [systemFinalDispos, setSystemFinalDispos] = useState([]);


	useEffect(() => {
		if (open) {
			console.log(segments)
			setLoading(true);
			for (const status of data) {
				const systemDispositions = [];
				const agentDispositions = [];

				for (const disposition of status.disposition.buckets) {
					if (disposition.agent.buckets) {
						let agentCount = 0;
						let systemCount = 0;
						for (const agent of disposition.agent.buckets) {
							if (systemAgents.includes(agent.key) || !agent.key) {
								systemCount = systemCount + agent.doc_count;
							} else {
								agentCount = agentCount + agent.doc_count;
							}
						}
						if (systemCount > 0) {
							systemDispositions.push({ label: disposition.key, value: systemCount });
						}
						if (agentCount > 0) {
							agentDispositions.push({ label: disposition.key, value: agentCount, agents: disposition.agent.buckets.sort((a, b) => b.doc_count - a.doc_count) });
							}
						}
					}
					if (status.key === 'retry') {
						// Sort from highest to lowest
						agentDispositions.sort((a, b) => b.value - a.value);
						systemDispositions.sort((a, b) => b.value - a.value);
						setSystemRetryDispos(systemDispositions);
					setAgentRetryDispos(agentDispositions);
				} else {
					// Sort from highest to lowest
					agentDispositions.sort((a, b) => b.value - a.value);
					systemDispositions.sort((a, b) => b.value - a.value);
					setSystemFinalDispos(systemDispositions);
					setAgentFinalDispos(agentDispositions);
				}
			}
			setLoading(false);
		}
	}, [open]);

	function GetRow(props) {
		const [open, setOpen] = useState(false);
		const { disposition, agent, status } = props;

		return (
			<>
				<TableRow key={disposition.label} sx={{ cursor: 'pointer' }}>
					{disposition?.agents && <TableCell onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}</TableCell>}
					<TableCell onClick={() => setOpen(!open)}>
						{disposition.label}
						{disposition.label === 'Agent Specific — Agent Unavailable' && <Tooltip title="This disposition appears when before placing the dial, there are no agents available to take the call. Instead of dialing the contact, the system will send in a suppressed disposition. These dials won't appear on your contact center reporting">
							<InfoOutlined color="primary" />
							</Tooltip>}
						</TableCell>
					<TableCell onClick={() => setOpen(!open)}>{disposition.value}</TableCell>
					<TableCell align="right">
						<Tooltip title='Download CSV'>
							<IconButton onClick={() => {
								downloadCSV(status, agent, disposition.label);
							}}>
								<FileDownloadOutlined color="primary" />
							</IconButton>
						</Tooltip>
					</TableCell>
				</TableRow>
				<TableRow key={disposition.label + 'details'} sx={{ display: open ? 'table-row' : 'none' }}>
					<TableCell colSpan={4}>
						<Table sx={{ width: '100%' }}>
							<TableHead>
								<TableRow>
									<TableCell>Agent</TableCell>
									<TableCell>Count</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{disposition?.agents?.map((agent) => (
									<TableRow key={agent.key}>
										<TableCell>{agent.key}</TableCell>
										<TableCell>{agent.doc_count}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableCell>
				</TableRow>
			</>
		);
	}

	const downloadCSV = async (status, agentType, dispositionFilter) => {
		let query = {
			"query": {
				"bool": {
					"must": [
						{
							"term": {
								"tenant.keyword": userContext.tenantId
							}
						},
						{
							"match_phrase": {
								"activityLogCampaignId": campaignId
							}
						},
						{
							"match_phrase": {
								"type": "Dial"
							}
						},
						{
							"range": {
								"createdAt": {
									"gte": inputStartDate ? inputStartDate : moment().startOf('day').toISOString(),
									"lte": inputEndDate ? inputEndDate : moment().toISOString()
								}
							}
						}
					],
					"must_not": [
						{
							"match_phrase": {
								"disposition": "Cadence Exhaustion - Retry Increment"
							}
						},
						{
							"match_phrase": {
								"disposition": "Agent Specific — Agent Unavailable"
							}
						}
					]
				}
			},
			"_source": [
				"disposition",
				"agent",
				"activityLogContactId",
				"metaData.contactExternalId",
				"activityLogSegmentId",
				"timestamp",
				"contactStatus"
			],
			"size": 10000
		};

		if (status) {
			query.query.bool.must.push({
				"match_phrase": {
					"contactStatus": status
				}
			});
		}

		if (dispositionFilter) {
			query.query.bool.must.push({
				"match_phrase": {
					"disposition": dispositionFilter
				}
			});
		}

		const results = await client.graphql({
			query: esQuery,
			variables: {
				model: 'activitylog-*',
				action: '_search',
				query: JSON.stringify(query)
			}
		});

		const resultsData = JSON.parse(results.data.esQuery).hits.hits;

		// Currently just hiding external id for Webex users - We should revist this later
		let csvData = `${userContext.telephonyProvider !== 'Webex' ? 'External Id,' : ''}Velocity Id,Disposition,Agent,Segment,Timestamp\n`;
		resultsData.forEach((result) => {
			const { metaData, activityLogContactId, agent, disposition, activityLogSegmentId, timestamp, contactStatus } = result._source;
			// Only add agents that match agentType
			if ((agentType && agentType === 'system' && (!systemAgents.includes(agent) && agent)) || (status && contactStatus !== status)) {
				return;
			} else if ((agentType && agentType === 'agent' && systemAgents.includes(agent)) || (status && contactStatus !== status)) {
				return;
			}

			// If disposition is has a value, filter list
			if (dispositionFilter && disposition != dispositionFilter) {
				return;
			}

			// Currently just hiding external id for Webex users - We should revist this later
			csvData += `${userContext.telephonyProvider !== 'Webex' ? metaData?.contactExternalId + ',' : ''}${activityLogContactId},${disposition},${agent},${segments.find((segment) => segment.segmentId === activityLogSegmentId)?.segmentName},${timestamp}\n`;
		});

		const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		let fileName = `${campaignName}_${inputStartDate ? inputStartDate : moment().startOf('day').toISOString()}_${inputEndDate ? inputEndDate : moment().toISOString()}`;
		if (dispositionFilter) {
			fileName = `${fileName}_${dispositionFilter}`;
		}
		if (status) {
			fileName = `${fileName}_${status}`;
		}
		if (agentType) {
			fileName = `${fileName}_${agentType}`;
		}
		fileName = `${fileName.replace(/:/g, '-')}.csv`;
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		// Clean up the blob URL after use
		URL.revokeObjectURL(url);

	}

	return (
		<Box display='flex' flexDirection="column" justifyContent='center' sx={{ width: '100%' }} gap={2}>
			{!loading && <Box sx={{ width: '100%' }} display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="Agent Disposition Final" action={
						<Box display='flex' gap={1} alignItems='center' justifyContent='center'>
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('completed', 'agent')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Retry dispositions seen here mean the contact met the max number attempts for the campaign">
								<InfoOutlined color="primary" />
							</Tooltip>
						</Box>
					} />
					<Table sx={{ width: '100%' }}>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{agentFinalDispos.map((disposition) => <GetRow disposition={disposition} status='completed' agent='agent' />)}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="Agent Disposition Retry"
						action={
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('retry', 'agent')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>}
					/>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{agentRetryDispos.map((disposition) => <GetRow disposition={disposition} status='retry' agent='agent' />)}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="System Disposition Final" action={
						<Box display='flex' gap={1} alignItems='center' justifyContent='center'>
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('completed', 'system')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Retry dispositions seen here mean the contact met the max number attempts for the campaign">
								<InfoOutlined color="primary" />
							</Tooltip>
						</Box>} />
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{systemFinalDispos.map((disposition) => <GetRow disposition={disposition} status='completed' agent='system' />)}

						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={cardStyle}>
					<CardHeader title="System Disposition Retry"
						action={
							<Tooltip title='Download CSV'>
								<IconButton onClick={() => downloadCSV('retry', 'system')}>
									<FileDownloadOutlined color="primary" />
								</IconButton>
							</Tooltip>}
					/>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell>Disposition</TableCell>
								<TableCell>Count</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{systemRetryDispos.map((disposition) => <GetRow disposition={disposition} status='retry' agent='system' />)}
						</TableBody>
					</Table>
				</Card>

			</Box>}
			{!loading && <Button sx={actionTwoButtonStyle} onClick={() => downloadCSV()} >Download All Data</Button>}
			{loading ? <CircularProgress /> : null}
		</Box>

	);
};

export default CampaignCallDetailsDialog;
